import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const Certification = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOpenModal = () => {
    setIsModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    document.body.style.overflow = 'unset';
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleCloseModal();
    }
  };

  const certificateInfo = {
    title: 'HACCP Codex',
    certificateNumber: '1652712252D3',
    issueDate: '27/12/2025',
    expiryDate: '26/12/2028',
    scope: 'Chế biến Trứng luộc',
    standard: 'TCVN 5603:2023 - Ref CXC 1-1969, revised 2020',
    issuer: 'BEST GLOBAL CERTIFICATION',
  };

  return (
    <>
      <div className="w-full py-8 md:py-16 bg-gray3">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
            {/* Certificate Preview - Left */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative w-full"
            >
              <div
                className="relative w-full h-[400px] md:h-[500px] lg:h-[600px] rounded-3xl overflow-hidden shadow-2xl group cursor-pointer border-4 border-white"
                onClick={handleOpenModal}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleOpenModal();
                  }
                }}
                role="button"
                tabIndex={0}
                aria-label="Xem giấy chứng nhận đầy đủ"
              >
                {/* PDF Preview */}
                <object
                  data="/CN CTY TRƯỜNG THỌ - SCAN.pdf#view=FitH&toolbar=0&navpanes=0"
                  type="application/pdf"
                  className="w-full h-full pointer-events-none group-hover:scale-105 transition-transform duration-500"
                  aria-label="Giấy chứng nhận HACCP"
                >
                  <iframe
                    src="/CN CTY TRƯỜNG THỌ - SCAN.pdf#view=FitH&toolbar=0&navpanes=0"
                    className="w-full h-full pointer-events-none group-hover:scale-105 transition-transform duration-500"
                    title="Giấy chứng nhận HACCP"
                  />
                </object>

                {/* Overlay on hover */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-300 flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 backdrop-blur-sm rounded-full p-4 md:p-6">
                    <svg
                      className="w-8 h-8 md:w-10 md:h-10 text-primary"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7"
                      />
                    </svg>
                  </div>
                </div>

                {/* Badge decoration */}
                <div className="absolute top-4 right-4 w-16 h-16 md:w-20 md:h-20 bg-primary/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg">
                  <svg
                    className="w-8 h-8 md:w-10 md:h-10 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
                    />
                  </svg>
                </div>
              </div>
            </motion.div>

            {/* Certificate Info - Right */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="flex flex-col gap-6 md:gap-8"
            >
              {/* Section Title */}
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <p className="uppercase text-gray2 text-[24px] md:text-[32px] font-bold">
                  Tiêu chuẩn HACCP CODEX
                </p>
              </motion.div>

              {/* Certificate Details */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="flex flex-col gap-4"
              >
                {/* Certificate Number */}
                <div className="p-5 bg-white rounded-2xl border-l-4 border-primary shadow-sm">
                  <p className="text-[12px] md:text-[14px] text-gray-500 uppercase tracking-wider font-semibold mb-3">
                    Số giấy chứng nhận
                  </p>
                  <p className="text-[18px] md:text-[20px] font-bold text-gray2">
                    {certificateInfo.certificateNumber}
                  </p>
                </div>

                {/* Standard */}
                <div className="p-5 bg-primary/5 rounded-2xl border border-primary/20">
                  <p className="text-[12px] md:text-[14px] text-primary uppercase tracking-wider font-semibold mb-2">
                    Tiêu chuẩn
                  </p>
                  <p className="text-[14px] md:text-[16px] text-gray-700 leading-relaxed">
                    {certificateInfo.standard}
                  </p>
                </div>
              </motion.div>

              {/* View Full Certificate Button */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="pt-2"
              >
                <button
                  onClick={handleOpenModal}
                  className="inline-flex items-center gap-2 bg-primary text-white px-6 py-3 rounded-full font-semibold text-[16px] md:text-[18px] hover:bg-primary/90 transition-all duration-300 shadow-lg hover:shadow-xl group"
                >
                  Xem giấy chứng nhận đầy đủ
                  <svg
                    className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7"
                    />
                  </svg>
                </button>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Modal for Full Certificate View */}
      <AnimatePresence>
        {isModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm"
            onClick={handleCloseModal}
            onKeyDown={handleKeyDown}
            tabIndex={-1}
          >
            {/* Close Button */}
            <button
              onClick={handleCloseModal}
              className="absolute top-4 right-4 md:top-8 md:right-8 z-10 w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm hover:bg-white/20 transition-all duration-300 flex items-center justify-center group"
              aria-label="Đóng"
            >
              <svg
                className="w-6 h-6 text-white group-hover:rotate-90 transition-transform duration-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            {/* PDF Container */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="relative w-full h-full max-w-6xl max-h-[90vh] bg-white rounded-2xl overflow-hidden shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              <object
                data="/CN CTY TRƯỜNG THỌ - SCAN.pdf#view=FitH"
                type="application/pdf"
                className="w-full h-full"
                aria-label="Giấy chứng nhận HACCP đầy đủ"
              >
                <iframe
                  src="/CN CTY TRƯỜNG THỌ - SCAN.pdf#view=FitH"
                  className="w-full h-full"
                  title="Giấy chứng nhận HACCP đầy đủ"
                />
              </object>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Certification;
